import React, { useState, useMemo } from 'react'
import { Row, Col, Table, Tag, Tooltip, Spin, Empty, Progress, Switch, Space } from 'antd'
import { WarningOutlined } from '@ant-design/icons'
import { AppCard } from '@/shared/components/ui'
import { useI18n } from '@/shared/i18n'
import { useDashboardStats, useRecentConversations } from '@/shared/hooks/useDashboardQueries'
import type { RecentConversation } from '@/shared/api/types'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import './Dashboard.scss'

dayjs.extend(relativeTime)

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

const shortId = (id: string): string => {
  const parts = id.split('_')
  return parts[parts.length - 1] || id.slice(-9)
}

const Dashboard: React.FC = () => {
  const { t } = useI18n('modules')
  const { data: stats, isLoading: statsLoading } = useDashboardStats()
  const { data: conversations, isLoading: convLoading } = useRecentConversations()
  const [filterLowScore, setFilterLowScore] = useState(false)

  const filteredConversations = useMemo(() => {
    if (!conversations) return []
    if (!filterLowScore) return conversations as RecentConversation[]
    return (conversations as RecentConversation[]).filter(
      c => c.satisfaction != null && c.satisfaction <= 2
    )
  }, [conversations, filterLowScore])

  const columns = [
    {
      title: 'ID',
      dataIndex: 'conversation_id',
      key: 'id',
      width: 120,
      render: (id: string) => (
        <Tooltip title={id}>
          <span className="conversation-id-short">{shortId(id)}</span>
        </Tooltip>
      ),
    },
    {
      title: t('dashboard.tables.columns.customer'),
      dataIndex: 'customer_name',
      key: 'customer',
      render: (name: string | undefined, record: RecentConversation) =>
        name || shortId(record.customer_platform_id),
    },
    {
      title: t('dashboard.tables.columns.platform'),
      dataIndex: 'platform',
      key: 'platform',
      width: 100,
      render: (platform: string) => <Tag>{platform}</Tag>,
    },
    {
      title: t('dashboard.tables.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => (
        <span className="status-badge">
          <span className={`status-dot ${status}`} />
          {status}
        </span>
      ),
    },
    {
      title: t('dashboard.tables.columns.satisfaction'),
      dataIndex: 'satisfaction',
      key: 'satisfaction',
      width: 80,
      align: 'center' as const,
      render: (score: number | null | undefined) => {
        if (score == null) return <span style={{ color: '#ccc' }}>-</span>
        const color = score <= 2 ? '#ff4d4f' : score <= 3 ? '#faad14' : '#52c41a'
        return <span style={{ color, fontWeight: 600 }}>{'⭐'.repeat(score)}</span>
      },
    },
    {
      title: t('dashboard.tables.columns.messages'),
      dataIndex: 'message_count',
      key: 'messages',
      width: 90,
      align: 'center' as const,
    },
    {
      title: t('dashboard.tables.columns.duration'),
      dataIndex: 'duration_seconds',
      key: 'duration',
      width: 90,
      render: (seconds: number) => formatDuration(seconds),
    },
    {
      title: t('dashboard.tables.columns.time'),
      dataIndex: 'last_activity',
      key: 'time',
      width: 140,
      render: (time: string) => (
        <Tooltip title={dayjs(time).format('YYYY-MM-DD HH:mm:ss')}>
          {dayjs(time).fromNow()}
        </Tooltip>
      ),
    },
  ]

  const platformEntries = stats ? Object.entries(stats.platform_distribution) : []
  const totalConv = stats?.total_conversations || 1

  return (
    <div className="dashboard">
      <Spin spinning={statsLoading}>
        <AppCard className="dashboard-overview">
          <div className="overview-stats">
            <div className="stat-item">
              <span className="stat-value">{stats?.total_conversations ?? '-'}</span>
              <span className="stat-label">{t('dashboard.statistics.totalConversations')}</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-value">{stats?.total_messages ?? '-'}</span>
              <span className="stat-label">{t('dashboard.statistics.totalMessages')}</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-value">{stats?.avg_messages_per_conversation ?? '-'}</span>
              <span className="stat-label">{t('dashboard.statistics.avgMessagesPerConv')}</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-value">{stats?.total_customers ?? '-'}</span>
              <span className="stat-label">{t('dashboard.statistics.customers')}</span>
            </div>
          </div>
        </AppCard>

        <Row gutter={[16, 16]} className="dashboard-info-row">
          <Col xs={24} lg={12}>
            <AppCard title={t('dashboard.today.title')}>
              <div className="info-item">
                <span className="info-label">{t('dashboard.today.conversations')}</span>
                <span className="info-value">{stats?.today_conversations ?? 0}</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('dashboard.today.messages')}</span>
                <span className="info-value">{stats?.today_messages ?? 0}</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('dashboard.today.lowSatisfaction')}</span>
                <span className="info-value">
                  {(stats?.today_low_satisfaction ?? 0) > 0 ? (
                    <span className="low-satisfaction-count">
                      <WarningOutlined /> {stats?.today_low_satisfaction}
                    </span>
                  ) : (
                    0
                  )}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('dashboard.today.knowledgeDocs')}</span>
                <span className="info-value">{stats?.knowledge_base?.total_documents ?? 0}</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('dashboard.today.knowledgeChunks')}</span>
                <span className="info-value">{stats?.knowledge_base?.total_chunks ?? 0}</span>
              </div>
            </AppCard>
          </Col>
          <Col xs={24} lg={12}>
            <AppCard title={t('dashboard.system.title')}>
              <div className="info-item">
                <span className="info-label">{t('dashboard.system.users')}</span>
                <span className="info-value">
                  {stats?.system?.active_users ?? 0} / {stats?.system?.total_users ?? 0}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('dashboard.system.connectors')}</span>
                <span className="info-value">{stats?.system?.connectors_configured ?? 0}</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('dashboard.system.platformIntegrations')}</span>
                <span className="info-value">{stats?.system?.platform_integrations ?? 0}</span>
              </div>
            </AppCard>
          </Col>
        </Row>
      </Spin>

      <Row gutter={[16, 16]} className="dashboard-table">
        <Col span={24}>
          <AppCard
            title={
              <Space>
                {t('dashboard.tables.recentConversations')}
                <Switch
                  size="small"
                  checked={filterLowScore}
                  onChange={setFilterLowScore}
                  checkedChildren={t('dashboard.tables.filterLowScore')}
                  unCheckedChildren={t('dashboard.tables.filterLowScore')}
                />
              </Space>
            }
          >
            <Table<RecentConversation>
              dataSource={filteredConversations}
              columns={columns}
              rowKey="conversation_id"
              size="small"
              pagination={false}
              loading={convLoading}
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: <Empty description={t('dashboard.tables.emptyText')} /> }}
            />
          </AppCard>
        </Col>
      </Row>

      {platformEntries.length > 0 && (
        <Row gutter={[16, 16]} className="dashboard-platform">
          <Col span={24}>
            <AppCard title={t('dashboard.platformDistribution')}>
              {platformEntries.map(([platform, count]) => (
                <div key={platform} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Tag>{platform}</Tag>
                    <span>
                      {count} ({Math.round(((count as number) / totalConv) * 100)}%)
                    </span>
                  </div>
                  <Progress
                    percent={Math.round(((count as number) / totalConv) * 100)}
                    showInfo={false}
                    size="small"
                  />
                </div>
              ))}
            </AppCard>
          </Col>
        </Row>
      )}
    </div>
  )
}

export default Dashboard
